// src\app\services\application\application.service.ts
import { inject, Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject, interval } from "rxjs";
import { UserService } from '../user/user.service';
import { AppConstants } from 'src/app/constants/app-constants';
import { AbstractApiService } from 'src/app/services/abstract-api-service/abstract-api-service';
import { AuthService } from '../auth/auth.service';
import { User } from 'src/app/model/User';
import { Application, ApplicationDto, ApplicationMapper } from 'src/app/model/Application';
import { APPLICATION_BORROWERS_ENDPOINT } from 'src/app/constants/api-routes';

/**
 * ApplicationService is used to hold/move application data from the API to the website
 */
@Injectable({
  providedIn: 'root'
})
export class ApplicationService extends AbstractApiService<Application> {
  protected _http = inject(HttpClient);
  protected _subject = new Subject<Application[]>();
  public applications$ = this._subject.asObservable();
  /** A list of applications the user has access to */
  protected _collection?: Application[];

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>("0:00");
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /**
   * Default constructor for the application.service, used to initialize services
   *
   * @param http
   * @param authService
   */
  constructor(
    private _userService: UserService,
    private _authService: AuthService,
  ) {
    super();
    // On authentication changes, clear persisted data
    this._userService.user$.subscribe((user: User | null) => {
      if (this._authService.authenticated && user != undefined) {
        this._fetchApplications();
      } else {
        this._collection = undefined;
        this._subject.next([]);
      }
    });

    // Continuously query the backend to receive new applications
    this._startTimer(0);
    this.timer.subscribe(
      (x: number) => {
        this._timers.splice(this._timers.indexOf(x));
        this._startTimer(x + 1);
        this._fetchApplications();
      });
  }

  /**
   * Get a list of applications
   *
   * @return A reference to the current applications
   */
  public getApplications(): Application[] | undefined {
    return this._collection?.map(x => x.copyOf());
  }

  /**
   * Set a list of applications
   *
   * @param applications
   */
  private _setApplications(applications: Application[]): void {
    this._collection = applications.map(x => x.copyOf());
    this._subject.next(applications.map(x => x.copyOf()));
  }


  /**
   * Get applications from the backend. You should subscribe to the 'applications$'
   * field to receive updates
   * 
   * @note This method could return early if the user is a borrower/supplier by calling the
   * user service (ex: `this._userService.userRole === 'borrower'`)
   */
  private _fetchApplications(): void {
    // Make sure the user is authenticated
    if (!this._authService.authenticated) return;

    // Get the user's active company
    const companyId = this._userService.company?.companyId;
    if (companyId == undefined) return;

    this._http.get<ApplicationDto[]>(APPLICATION_BORROWERS_ENDPOINT(companyId)).subscribe({
      next: (applicationInterfaces: ApplicationDto[]): void => {
        if (applicationInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into application list`);
        }

        // Convert the interfaces to class values
        let applications: Application[] = [];
        for (let appInt of applicationInterfaces) {
          applications.push(ApplicationMapper.toModel(appInt));
        }

        // Propagate changes if different
        this._setApplications(applications);
        console.log(applications)
        console.log(`[INFO]: successfully queried applications`);
      },
    });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await delay(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(`${minutes}:${(""+seconds).length === 1 ? "0" : ""}${seconds}`);
    }
  }
}

// Helper function to delay for a given number of milliseconds
const delay = (delay: number): Promise<void> => {
  return new Promise(r => {setTimeout(r, delay)});
}
